import { simpleDate } from 'lib/date';
import PortalConnection from 'models/PortalConnection';
import { PortalConnectionBlocker } from 'services/api/portalConnections';

// The two axes that only exist when the server signs in on the user's behalf. In extension mode
// the password lives in chrome.storage.local and never reaches Olio at all, so a
// missing-credentials blocker there describes a thing that is not supposed to exist.
const CREDENTIAL_BLOCKERS: PortalConnectionBlocker[] = ['no_credentials', 'credentials_invalid'];

const BLOCKER_LABELS: Record<PortalConnectionBlocker, string> = {
  no_credentials: 'No credentials',
  credentials_invalid: 'Password rejected',
  no_retrieval_script: 'No instructions for reading a referral',
  no_discovery_script: 'No instructions for finding referrals',
  manual_mode: 'Handled by hand',
  no_coordinating_group: 'No coordinating group to receive results',
};

/**
 * The blockers that mean anything for THIS portal's mode. The server already decides which axes
 * apply, so this is a guard rather than a second derivation: it drops the credential axis outside
 * headless mode, and drops `manual_mode` everywhere — a client that chose to work a portal by hand
 * has configured it, not broken it.
 */
export function applicableBlockers(connection: PortalConnection): PortalConnectionBlocker[] {
  return connection.blockers.filter((blocker) => {
    if (blocker === 'manual_mode') return false;
    if (!connection.isHeadless && CREDENTIAL_BLOCKERS.includes(blocker)) return false;
    return true;
  });
}

// Saved, and nothing has come back about it. Verification is an async login attempt whose only
// return path is the status mirror, and the MFA outcomes (otp, captcha, confused) push nothing at
// all — so this state is permanent for some portals, not a moment in flight.
export function awaitingConfirmation(connection: PortalConnection): boolean {
  const credentials = connection.credentials;
  return connection.isHeadless && credentials?.status === 'active' && !credentials.verifiedAt;
}

export type PortalBadge = 'verified' | 'needsAction' | null;

/**
 * Three outcomes, not two. `VERIFIED` claims Olio confirmed the sign-in and `NEEDS ACTION` asks
 * the user for something — a saved-but-unconfirmed portal is neither, so it carries NO badge:
 * the user did their part and we have not confirmed ours.
 */
export function portalBadge(connection: PortalConnection): PortalBadge {
  if (applicableBlockers(connection).length > 0) return 'needsAction';
  if (awaitingConfirmation(connection)) return null;
  return 'verified';
}

export const BADGE_LABELS: Record<'verified' | 'needsAction', string> = {
  verified: 'VERIFIED',
  needsAction: 'NEEDS ACTION',
};

function credentialsPhrase(connection: PortalConnection): string | null {
  if (!connection.isHeadless) return null;
  return awaitingConfirmation(connection) ? 'Credentials saved, not yet confirmed' : 'Credentials provided';
}

function lastRecordedPhrase(connection: PortalConnection): string | null {
  const dates = [connection.recording('discovery').recordedAt, connection.recording('retrieval').recordedAt].filter(
    (date): date is Date => !!date
  );
  if (dates.length === 0) return null;
  const newest = dates.reduce((latest, date) => (date > latest ? date : latest));
  return `Last recorded on ${simpleDate(newest)}`;
}

/**
 * The row's sub-line. It reads off `blockers` while any apply — the server's own reasons, in the
 * server's own order — and only describes what IS set up once none do.
 */
export function portalStatusLine(connection: PortalConnection): string {
  const blockers = applicableBlockers(connection);
  if (blockers.length > 0) return blockers.map((blocker) => BLOCKER_LABELS[blocker]).join(' • ');

  const phrases = [credentialsPhrase(connection), lastRecordedPhrase(connection)].filter(Boolean);
  // Never blank: a row with a name and nothing under it reads as a load failure.
  return phrases.length > 0 ? phrases.join(' • ') : 'Ready';
}

// The portals this modal manages. Manual mode has nothing to configure — no credentials, no
// recording — so listing it would offer a form that changes nothing. The server already
// excludes manual rows from /portal_connections (OLIO-3018); this guard keeps the rule
// stated where the modal reads it.
export function manageablePortals(connections: PortalConnection[]): PortalConnection[] {
  return connections.filter((connection) => !connection.isManual);
}
